import React from "react";
import { FiHome, FiMail, FiPhone } from "react-icons/fi";
import { SiSignal } from "react-icons/si";

const toInternationalPhone = (phone) => {
  if (!phone) return null;

  const cleaned = String(phone).replace(/[^\d+]/g, "");
  if (!cleaned) return null;

  if (cleaned.startsWith("+")) return cleaned;
  if (cleaned.startsWith("00")) return `+${cleaned.slice(2)}`;
  if (cleaned.startsWith("0")) return `+31${cleaned.slice(1)}`;

  return cleaned;
};

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && String(value).trim() !== "");

const TeacherContactCard = ({ t, contact = {}, teacherName }) => {
  const email = firstValue(contact.email, contact.emailAddress);
  const phone = firstValue(contact.phone, contact.phoneNumber, contact.mobilePhone);
  const addressLine = firstValue(contact.addressLine, contact.address, contact.street);
  const locationLine = [firstValue(contact.zipCode, contact.postalCode), contact.city].filter(Boolean).join(" ");
  const address = [addressLine, locationLine].filter(Boolean).join(", ");
  const signalPhone = toInternationalPhone(phone);
  const hasContactDetails = Boolean(email || phone || address);

  return (
    <div className="mt-5 rounded-2xl border border-slate-100 bg-slate-50/40 p-4 dark:border-slate-800 dark:bg-slate-900/30">
      <div>
        <p className="text-sm font-bold text-slate-950 dark:text-white">
          {t("parentPortal.children.teacher_contact", { defaultValue: "Teacher contact" })}
        </p>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {hasContactDetails
            ? t("parentPortal.children.teacher_contact_hint", { defaultValue: "Contact details shared by the teacher." })
            : t("parentPortal.teacher_no_shared_contact", { defaultValue: "Teacher has not shared contact details." })}
        </p>
      </div>

      {hasContactDetails && (
        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-100 bg-white dark:border-slate-800 dark:bg-slate-950/30">
          {email && (
            <a
              href={`mailto:${email}`}
              className="flex min-w-0 items-center gap-3 border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-emerald-50/60 dark:border-slate-800 dark:text-slate-200 dark:hover:bg-emerald-950/20"
            >
              <FiMail className="h-4 w-4 shrink-0 text-emerald-700 dark:text-emerald-300" aria-hidden="true" />
              <span className="truncate">{email}</span>
            </a>
          )}

          {phone && (
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
              <a
                href={`tel:${phone}`}
                className="flex min-w-0 items-center gap-3 text-sm font-semibold text-slate-700 transition hover:text-emerald-800 dark:text-slate-200 dark:hover:text-emerald-200"
              >
                <FiPhone className="h-4 w-4 shrink-0 text-emerald-700 dark:text-emerald-300" aria-hidden="true" />
                <span className="truncate">{phone}</span>
              </a>

              {signalPhone && (
                <a
                  href={`https://signal.me/#p/${encodeURIComponent(signalPhone)}`}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t("parentPortal.children.openSignal", { defaultValue: "Open Signal" })}
                  title={t("parentPortal.children.openSignal", { defaultValue: "Open Signal" })}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-50 text-[#3A76F0] transition hover:bg-blue-50 dark:bg-slate-900"
                >
                  <SiSignal className="h-4 w-4" aria-hidden="true" />
                </a>
              )}
            </div>
          )}

          {address && (
            <div className="flex min-w-0 items-center gap-3 px-4 py-3 text-sm font-semibold text-slate-700 dark:text-slate-200">
              <FiHome className="h-4 w-4 shrink-0 text-emerald-700 dark:text-emerald-300" aria-hidden="true" />
              <span className="truncate">{address}</span>
            </div>
          )}
        </div>
      )}

      {!hasContactDetails && teacherName && (
        <p className="mt-4 text-sm font-semibold text-slate-700 dark:text-slate-200">{teacherName}</p>
      )}
    </div>
  );
};

export default TeacherContactCard;
